import { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import SongList from "components/SongList/SongList";
import { actions } from "state/slices/songsSlice";
import { LoadingSpinner } from "components/LoadingSpinner/LoadingSpinner";
import { ICON_STROKE, SearchIcon } from "shared/icons";

interface OwnProps {
  songs: Song[];
  selectSongHandler: (song: Song) => void;
  initialSearchString: string;
}

interface DispatchProps {
  onSetSearchString: typeof actions.setSearchString;
  onSearchForMoreResults: typeof actions.searchForMoreResults;
}

export type SearchableSongListProps = OwnProps & ResolveThunks<DispatchProps>;

export function SearchableSongList(
  props: SearchableSongListProps
): JSX.Element {
  const searchTimer = useRef<NodeJS.Timeout | null>(null);
  const [localSearchString, setLocalSearchString] = useState(
    props.initialSearchString
  );
  const [isSearching, setIsSearching] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  // At most one search request at a time. A search asked for while one is in
  // flight waits here (only the newest is kept) and is sent when that settles.
  //
  // This replaces an effect keyed on `isSearching`, which fired on both edges:
  // once when a search started and again when its spinner cleared, so every
  // search hit the server twice. That second request was also, by accident,
  // the only thing that caught up with typing done mid-search.
  const inFlight = useRef(false);
  const queued = useRef<string | null>(null);

  function search(searchString: string, showSpinner: boolean): void {
    if (inFlight.current) {
      queued.current = searchString;
      return;
    }

    inFlight.current = true;
    if (showSpinner) {
      setIsSearching(true);
    }

    // The thunk calls this whether the request succeeds or fails.
    props.onSetSearchString(searchString, () => {
      inFlight.current = false;
      const next = queued.current;
      queued.current = null;

      if (next !== null && next !== searchString) {
        search(next, true);
      } else {
        setIsSearching(false);
      }
    });
  }

  // Refresh the current search once on mount, without the spinner, as before.
  // Mount-only on purpose: later searches come from typing, below.
  useEffect(() => {
    search(props.initialSearchString, false);

    return () => {
      if (searchTimer.current !== null) {
        clearTimeout(searchTimer.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  console.log(`Songs in list ${props.songs?.length}`);

  useEffect(() => {
    if (isAdding) {
      props.onSearchForMoreResults(() => setIsAdding(false));
    }
  }, [isAdding, props.onSearchForMoreResults]);

  function render(): JSX.Element {
    return (
      <div className="p-2 sm:p-4 w-full flex flex-col mt-1">
        {renderSearch()}
        {isSearching || isAdding ? (
          <LoadingSpinner border={false} />
        ) : (
          <SongList
            songs={props.songs}
            otherdata={
              props.songs[0]?.playlistCount
                ? props.songs.map((s) => `${s.playlistCount}`)
                : undefined
            }
            otherdataName={
              props.songs[0]?.playlistCount ? "Relevance" : undefined
            }
            selectSongHandler={props.selectSongHandler}
          />
        )}
      </div>
    );
  }

  function renderSearch(): JSX.Element {
    return (
      <div className="mb-4 flex flex-row items-center gap-2">
        {/* The input had `focus:outline-none` (plus `focus:shadow-outline`,
            which doesn't exist in Tailwind 3), so it had no focus state at
            all. The "+" beside it was a clickable <span> the keyboard could
            not reach. */}
        <input
          onChange={(evt) => {
            const value = evt.target.value;
            setLocalSearchString(value);

            if (searchTimer.current !== null) {
              clearTimeout(searchTimer.current);
            }

            searchTimer.current = setTimeout(() => {
              search(value, true);
            }, 1000);
          }}
          type="text"
          className="field-input"
          placeholder="Search songs or artists"
          aria-label="Search songs"
          value={localSearchString}
        />
        <button
          type="button"
          className="button-generic flex items-center gap-2 text-sm whitespace-nowrap flex-none"
          onClick={() => {
            setIsAdding(true);
          }}
          title="Search Spotify for more matches"
        >
          <SearchIcon size={16} strokeWidth={ICON_STROKE} aria-hidden="true" />
          More from Spotify
        </button>
      </div>
    );
  }

  return render();
}

const mapDispatch: DispatchProps = {
  onSetSearchString: actions.setSearchString,
  onSearchForMoreResults: actions.searchForMoreResults,
};

export default connect(undefined, mapDispatch)(SearchableSongList);
